/**
 * <CancelFeesCard> — le montant des frais de résiliation (molecule).
 *
 * La carte centrale de la troisième étape : le montant en grand, ce qui le compose, et la date
 * d'engagement dessous. Deux tons, un seul composant — rouge quand il y a des frais, vert quand
 * il n'y en a pas, parce que c'est la même information qui répond « combien ».
 *
 * Elle ne calcule rien : `amountLabel`, `detail` et `dateLine` arrivent déjà écrits (le tarif par
 * mois d'engagement est une règle métier, elle ne vit pas dans le design system). Composition de
 * `<Card>` + `<Text>` + `<Divider>` — aucune surface dessinée à la main.
 */
import React from "react"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Divider } from "../../atoms/Divider"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../Card"

export type CancelFeesCardProps = {
    /** Des frais restent à régler — décide du ton de la carte. */
    hasFees: boolean
    /** Le montant, déjà formaté — « 59,70 € ». */
    amountLabel: string
    /** Ce qui compose le montant, ou la raison de sa gratuité. */
    detail: string
    /** La date d'engagement, en une phrase ; absente, la carte s'arrête au détail. */
    dateLine?: string | null
    /** Le crédit de la famille déduit du montant, une phrase par ligne ; absent, rien. */
    creditLines?: string[]
    testID?: string
}

/** Le libellé du montant ne dépend d'aucune donnée : il nomme la carte. */
const AMOUNT_LABEL = "Frais de résiliation"

export function CancelFeesCard(props: CancelFeesCardProps) {
    const { hasFees, amountLabel, detail, dateLine, creditLines = [], testID } = props

    const surface: SemanticColorKey = hasFees ? "dangerLight" : "successLight"
    const ink: SemanticColorKey = hasFees ? "danger-strong" : "success-strong"

    return (
        <Card testID={testID} background={surface} p="4">
            <Stack gap="2" align="center">
                <Text size="text-sm" weight="semibold" color={ink}>
                    {AMOUNT_LABEL}
                </Text>
                <Text size="text-3xl" weight="extrabold" color={ink} center>
                    {amountLabel}
                </Text>
                <Text size="text-sm" color={ink} center>
                    {detail}
                </Text>
                {creditLines.map((line) => (
                    <Text key={line} size="text-sm" weight="semibold" color={ink} center>
                        {line}
                    </Text>
                ))}
                {dateLine ? (
                    <>
                        <Divider color={hasFees ? "danger" : "success"} spacing="1" />
                        <Text size="text-sm" color={ink} center>
                            {dateLine}
                        </Text>
                    </>
                ) : null}
            </Stack>
        </Card>
    )
}
